import React from 'react'
import SimpleTextViewer from '../../../../../../../components/common/text-viewer/SimpleTextViewer'
import { Flex } from 'antd'
import Text from 'antd/es/typography/Text'
import { useAnswerContext } from './AnswerContext'
import { getRelevantAnswer } from '../../../../../../../utils/question.helper'
import { getColorByIndex } from '../../../../../../../utils/colors.helper'
import { useThemeColors } from '../../../../../../../hooks/useThemeColors'
import { useExplanation } from '../../../../../../../store/apis/utils/hooks/category.hooks'
import { useSelector } from 'react-redux'
import textSubmissionDrawerSelectors from '../../../../../../../store/selectors/text-submission-drawer.selectors'
import SubmissionAttachments from './components/SubmissionAttachments'
import useRubricContext from '../../../../../../../hooks/useRubricContext'
import { useFetchSubmissionAttachmentsQuery } from '../../../../../../../store/apis/submission.api'

const useHighlights = ({ question, answers }) => {
    const colors = useThemeColors()
    const focusedQuestionRubricId = useSelector(textSubmissionDrawerSelectors.focusedQuestionRubricId)
    const { categoryIdToHighlight } = useAnswerContext()
    const { getCategoryById } = useRubricContext()

    // Only show highlights if the focused rubric belongs to this question
    const questionRubricIds = question?.questionRubrics?.map(qr => qr.id) || []
    const isFocusedRubricInThisQuestion = focusedQuestionRubricId && questionRubricIds.includes(focusedQuestionRubricId)

    const answer = isFocusedRubricInThisQuestion 
        ? getRelevantAnswer(answers, question, focusedQuestionRubricId) 
        : null

    const categoryToHighlight = categoryIdToHighlight ? getCategoryById(categoryIdToHighlight) : null
    const currentCategoryId = answer?.categoryId

    const { explanation, isFetching } = useExplanation(answer)

    const mapHighlightRanges = (ranges, isAdequate) =>
        ranges?.map(({ start, end, importance }) => ({
            start,
            end,
            opacity: importance / 2,
            color: isAdequate ? getColorByIndex(categoryToHighlight?.colorId).value : colors.warningBackground,
            textColor: isAdequate ? null : colors.warningText,
        })) || []

    const highlightRanges = (() => {
        if (!answer) return []
        if (categoryIdToHighlight === currentCategoryId)
            return mapHighlightRanges(answer?.highlights, answer?.isAdequate)
        return mapHighlightRanges(explanation?.highlights, explanation?.isAdequate)
    })()

    return { highlightRanges, isFetching }
}

const AnswerEditor = ({ submissionId, answers, question, maxHeight }) => {
    const { highlightRanges, isFetching: isFetchingHighlights } = useHighlights({ question, answers })

    const editorTitle = (
        <Flex justify='space-between' className='f-width'>
            <Text>Answer</Text>
            {isFetchingHighlights && <Text type='secondary'>Fetching highlights...</Text>}
        </Flex>
    )

    const answer = getRelevantAnswer(answers, question)
    const hasTextContent = !!answer?.content?.trim()

    // When there is no text, check if this is a file-upload question (has attachments)
    // vs a text question where the student submitted nothing.
    // RTK Query deduplicates this call — SubmissionAttachments fires the same query.
    const { data: allAttachments, isLoading: isLoadingAttachments } = useFetchSubmissionAttachmentsQuery(
        { submissionId },
        { skip: hasTextContent },
    )
    const hasAttachments = !hasTextContent &&
        (allAttachments?.some(a => a.questionId === question.id) ?? false)

    // File-upload question: no text content but has attachments → attachments-only layout
    if (!hasTextContent && !isLoadingAttachments && hasAttachments) {
        return (
            <Flex vertical gap={8} style={{ padding: '8px 0' }}>
                <SubmissionAttachments submissionId={submissionId} questionId={question.id} />
            </Flex>
        )
    }

    // Text question with empty submission → keep the familiar empty text box, no attachments
    if (!hasTextContent) {
        return (
            <SimpleTextViewer
                title={editorTitle}
                value=""
                wrapperStyles={{ height: '100%' }}
                showActions={false}
                maxHeight={maxHeight}
                fontSize={16}
            />
        )
    }

    return (
        <SimpleTextViewer
            title={editorTitle}
            value={answer.content}
            wrapperStyles={{ height: '100%' }}
            showActions
            wordsLimit={question.wordsLimit}
            showWordsCount
            maxHeight={maxHeight}
            highlightRanges={highlightRanges}
            footer={<SubmissionAttachments submissionId={submissionId} questionId={question.id} />}
            fontSize={16}
        />
    )
}

export default AnswerEditor
